import React, { useState } from 'react';
import { Card, Button, Table, Tag, Radio, Space, InputNumber, Switch, Alert, Progress, Badge, message, Tooltip, Input, Row, Col, Collapse, Typography } from 'antd';
import { SearchOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, AppleOutlined, LinkOutlined, SaveOutlined } from '@ant-design/icons';
import apiClient from '../../server/server';

const { Text } = Typography;
const { Panel } = Collapse;

const ITunesIdManager = () => {
  // Search state
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(50);
  const [autoUpdate, setAutoUpdate] = useState(true);

  // Results
  const [autoMatched, setAutoMatched] = useState([]);
  const [needsReview, setNeedsReview] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [summary, setSummary] = useState(null);

  // Review selections: { [profileId]: selectedItunesId }
  const [reviewSelections, setReviewSelections] = useState({});
  const [assigningId, setAssigningId] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());

  // Manual search for "not found"
  const [manualSearching, setManualSearching] = useState(null);
  const [manualSearchQuery, setManualSearchQuery] = useState('');

  // ── Batch Search ──
  const handleBatchSearch = async () => {
    setLoading(true);
    setAutoMatched([]);
    setNeedsReview([]);
    setNotFound([]);
    setSummary(null);
    setReviewSelections({});
    setAssignedIds(new Set());

    try {
      const response = await apiClient.post('/ai/batch-itunes-search', { limit, autoUpdate });
      const data = response.data;

      setAutoMatched(data.autoMatched || []);
      setNeedsReview(data.needsReview || []);
      setNotFound(data.notFound || []);
      setSummary(data.summary || null);

      const total = (data.autoMatched?.length || 0) + (data.needsReview?.length || 0) + (data.notFound?.length || 0);
      message.success(`Processed ${total} profiles: ${data.autoMatched?.length || 0} matched, ${data.needsReview?.length || 0} need review, ${data.notFound?.length || 0} not found`);
    } catch (err) {
      message.error('Batch search failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Assign iTunes ID ──
  const handleAssign = async (profileId) => {
    const itunesId = reviewSelections[profileId];
    if (!itunesId) {
      message.warning('Select a candidate first');
      return;
    }

    setAssigningId(profileId);
    try {
      await apiClient.post('/ai/update-itunes-id', { profileId, itunesId });
      setAssignedIds(prev => new Set([...prev, profileId]));
      message.success('iTunes ID assigned');
    } catch (err) {
      message.error('Failed to assign: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssigningId(null);
    }
  };

  const itunesLink = (id) => `https://music.apple.com/artist/${id}`;

  const confidenceColor = (c) => {
    if (c === 'exact_electronic') return 'green';
    if (c === 'exact_single') return 'cyan';
    if (c === 'single_result') return 'blue';
    return 'default';
  };

  const hasResults = autoMatched.length > 0 || needsReview.length > 0 || notFound.length > 0;

  // ── Auto-Matched Table ──
  const autoMatchedColumns = [
    {
      title: 'Artist',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <strong>{name}</strong>
    },
    {
      title: 'iTunes ID',
      dataIndex: 'itunesId',
      key: 'itunesId',
      width: 140,
      render: (id) => (
        <a href={itunesLink(id)} target="_blank" rel="noopener noreferrer">
          <Tag icon={<AppleOutlined />} color="purple">{id}</Tag>
        </a>
      )
    },
    {
      title: 'iTunes Name',
      dataIndex: 'itunesName',
      key: 'itunesName',
      width: 180,
      ellipsis: true
    },
    {
      title: 'Genre',
      dataIndex: 'genre',
      key: 'genre',
      width: 120,
      render: (g) => <Tag>{g}</Tag>
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 140,
      render: (c) => <Tag color={confidenceColor(c)}>{c}</Tag>
    },
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: () => autoUpdate ? <Tag color="success" icon={<CheckCircleOutlined />}>Saved</Tag> : <Tag>Pending</Tag>
    }
  ];

  // ── Not Found Table ──
  const notFoundColumns = [
    {
      title: 'Artist',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <strong>{name}</strong>
    },
    {
      title: 'Action',
      key: 'action',
      width: 320,
      render: (_, record) => {
        const isAssigned = assignedIds.has(record.profileId);
        if (isAssigned) return <Tag color="success">Marked</Tag>;

        return (
          <Space>
            <Tooltip title="Search on Apple Music">
              <Button
                size="small"
                icon={<SearchOutlined />}
                onClick={() => window.open(`https://music.apple.com/search?term=${encodeURIComponent(record.name)}`, '_blank')}
              >
                Manual Search
              </Button>
            </Tooltip>
            <Button
              size="small"
              icon={<CloseCircleOutlined />}
              loading={assigningId === record.profileId}
              onClick={async () => {
                setAssigningId(record.profileId);
                try {
                  await apiClient.post('/ai/update-itunes-id', { profileId: record.profileId, itunesId: 'none' });
                  setAssignedIds(prev => new Set([...prev, record.profileId]));
                  message.success(`Marked "${record.name}" as no iTunes`);
                } catch (err) {
                  message.error('Failed: ' + (err.response?.data?.message || err.message));
                } finally {
                  setAssigningId(null);
                }
              }}
            >
              No iTunes
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      {/* Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space>
              <Text strong>Limit:</Text>
              <InputNumber min={1} max={200} value={limit} onChange={setLimit} disabled={loading} style={{ width: 80 }} />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text strong>Auto-save matches:</Text>
              <Switch checked={autoUpdate} onChange={setAutoUpdate} disabled={loading} />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={handleBatchSearch}
              size="large"
            >
              {loading ? 'Searching...' : 'Run Batch Search'}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Summary */}
      {summary && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space size="large">
              <span>Total: <strong>{summary.total}</strong></span>
              <Badge status="success" text={<span>Auto-matched: <strong>{summary.autoMatched}</strong></span>} />
              <Badge status="warning" text={<span>Needs review: <strong>{summary.needsReview}</strong></span>} />
              <Badge status="error" text={<span>Not found: <strong>{summary.notFound}</strong></span>} />
              {summary.autoUpdated && <Tag color="green">Auto-saved to DB</Tag>}
            </Space>
          }
        />
      )}

      {/* Auto-Matched Section */}
      {autoMatched.length > 0 && (
        <Collapse defaultActiveKey={needsReview.length > 0 ? [] : ['auto']} style={{ marginBottom: 16 }}>
          <Panel
            header={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /><span>Auto-Matched ({autoMatched.length})</span></Space>}
            key="auto"
          >
            <Table
              dataSource={autoMatched}
              columns={autoMatchedColumns}
              rowKey="profileId"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
            />
          </Panel>
        </Collapse>
      )}

      {/* Needs Review Section */}
      {needsReview.length > 0 && (
        <Card
          title={<Space><WarningOutlined style={{ color: '#faad14' }} /><span>Needs Review ({needsReview.length})</span></Space>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {needsReview.map((item) => {
            const isAssigned = assignedIds.has(item.profileId);

            return (
              <Card
                key={item.profileId}
                size="small"
                style={{
                  marginBottom: 12,
                  borderLeft: isAssigned ? '3px solid #52c41a' : '3px solid #faad14',
                  background: isAssigned ? '#f6ffed' : undefined
                }}
              >
                <Row align="middle" gutter={16}>
                  <Col flex="200px">
                    <Text strong style={{ fontSize: 15 }}>{item.name}</Text>
                    {isAssigned && <Tag color="success" style={{ marginLeft: 8 }}>Assigned</Tag>}
                  </Col>
                  <Col flex="auto">
                    <Radio.Group
                      value={reviewSelections[item.profileId]}
                      onChange={(e) => setReviewSelections(prev => ({ ...prev, [item.profileId]: e.target.value }))}
                      disabled={isAssigned}
                    >
                      <Space direction="vertical" size={4}>
                        {(item.candidates || []).map((c) => (
                          <Radio key={c.itunesId} value={c.itunesId}>
                            <Space size={8}>
                              <a href={itunesLink(c.itunesId)} target="_blank" rel="noopener noreferrer">
                                <Tag icon={<AppleOutlined />} color="purple">{c.itunesId}</Tag>
                              </a>
                              <span>{c.artistName}</span>
                              <Tag>{c.genre || 'Unknown'}</Tag>
                            </Space>
                          </Radio>
                        ))}
                        <Radio value="none">
                          <Tag color="default" icon={<CloseCircleOutlined />}>No iTunes — skip this artist</Tag>
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Col>
                  <Col>
                    {!isAssigned && (
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        size="small"
                        loading={assigningId === item.profileId}
                        disabled={!reviewSelections[item.profileId]}
                        onClick={() => handleAssign(item.profileId)}
                      >
                        Assign
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card>
            );
          })}
        </Card>
      )}

      {/* Not Found Section */}
      {notFound.length > 0 && (
        <Collapse style={{ marginBottom: 16 }}>
          <Panel
            header={<Space><CloseCircleOutlined style={{ color: '#ff4d4f' }} /><span>Not Found ({notFound.length})</span></Space>}
            key="notfound"
          >
            <Table
              dataSource={notFound}
              columns={notFoundColumns}
              rowKey="profileId"
              size="small"
              pagination={false}
            />
          </Panel>
        </Collapse>
      )}

      {/* Empty state */}
      {!loading && !hasResults && (
        <Alert
          message="iTunes ID Manager"
          description="Click 'Run Batch Search' to find and assign iTunes IDs for artist profiles. It searches profiles without an iTunes ID and auto-matches when confident."
          type="info"
          showIcon
        />
      )}
    </div>
  );
};

export default ITunesIdManager;
